import {ActionTree, GetterTree, Module, MutationTree} from "vuex";
import {RootState} from "../store";
import * as turf from "@turf/turf";
import {MeasurementPrefUtility} from "../../../../utils/helpers/MeasurementPref";
import {HikeTimeUtility} from "../../../../utils/helpers/HikeTime";
import {HiikerColor} from "../../../../utils/helpers/HiikerColor";
import measurementMarker from "../../../../images/measurement_marker.png";
import mapboxgl from "mapbox-gl";
import {singleHiikerElevation} from "../../../../utils/helpers/HiikerElevationApiHelper";
import {elevationGainFromCoordinates} from "../../../../utils/elevation/elevationGain";

// State for the measurement tool
export class HikeMeasurementState {
  measurementMode: boolean = false;
  measuredGeoJson: any = null;
  measureableGeoJson: any = null; // The multiline GeoJSON data for the hike
  markers: mapboxgl.Marker[] = [];
  isActive: boolean = false;
  hoverCoordinate: [number, number] | null = null; // LngLat for hover position
}

let markers: mapboxgl.Marker[] = [];
let markerElevations: number[] = []; // Store elevation for each marker
let clickHandler: ((e: mapboxgl.MapMouseEvent) => void) | null = null; // Store reference to click handler

// Cache the trail network graph by measureableGeoJson reference so repeated
// measurements / marker drags on the same hike don't rebuild it each time.
let cachedGraphGeoJson: any = null;
let cachedGraph: TrailGraph | null = null;

function getMeasurementGraph(geoJson: any, segments: any[]): TrailGraph {
  if (cachedGraph && cachedGraphGeoJson === geoJson) return cachedGraph;
  cachedGraph = buildTrailGraph(segments.map((s: any) => s.geometry.coordinates));
  cachedGraphGeoJson = geoJson;
  return cachedGraph;
}

// Route styles the measurement tool is allowed to snap to / trace along.
// Includes the main route plus alternates and spurs so users can measure
// those trails too. 'transport' links (dashed connectors) are excluded.
export const MEASURABLE_ROUTE_STYLES = ['route', 'alt', 'spur'];

export function isMeasurableRouteStyle(routeStyle: string | undefined | null): boolean {
  return MEASURABLE_ROUTE_STYLES.includes(routeStyle || 'route');
}

// ---------------------------------------------------------------------------
// Trail-network routing
//
// To let a measurement cross flexibly between trails (e.g. start on the main
// route and finish on an alt or spur), we treat every measurable trail as part
// of one connected graph. Each trail vertex is a node; consecutive vertices are
// edges weighted by their ground distance. Where trails meet but don't share an
// exact coordinate, we stitch them together within a small tolerance (see
// BRIDGE_TOLERANCE_M) so the network stays connected and a shortest-path search
// can flow from one trail onto another.
// ---------------------------------------------------------------------------

interface TrailGraphNode {
  coord: number[]; // representative [lng, lat, ele?]
  edges: { to: string; w: number }[];
}
export type TrailGraph = Map<string, TrailGraphNode>;

// How close a trail endpoint must come to another trail for us to treat them as
// joined at a junction. Alts/spurs are digitised independently from the main
// route, so their junction point is rarely the exact same coordinate — this
// tolerance lets us stitch them together anyway.
const BRIDGE_TOLERANCE_M = 25;

// Quantise a coordinate to ~1m so vertices that are shared (or all-but-identical)
// between trails at a junction collapse onto the same node and become connected.
function trailNodeKey(coord: number[]): string {
  return `${coord[0].toFixed(5)},${coord[1].toFixed(5)}`;
}

function metresBetween(a: number[], b: number[]): number {
  return turf.distance(turf.point([a[0], a[1]]), turf.point([b[0], b[1]]), { units: 'meters' });
}

export function buildTrailGraph(segments: number[][][]): TrailGraph {
  const graph: TrailGraph = new Map();

  const ensureNode = (coord: number[]): string => {
    const key = trailNodeKey(coord);
    if (!graph.has(key)) {
      graph.set(key, { coord, edges: [] });
    }
    return key;
  };

  const addEdge = (from: string, to: string, w: number) => {
    const node = graph.get(from);
    if (!node || from === to) return;
    if (!node.edges.some((e) => e.to === to)) {
      node.edges.push({ to, w });
    }
  };

  // 1. Edges along each trail (consecutive vertices).
  segments.forEach((coords) => {
    if (!coords || coords.length < 2) return;
    for (let i = 0; i < coords.length - 1; i++) {
      const a = ensureNode(coords[i]);
      const b = ensureNode(coords[i + 1]);
      if (a === b) continue;
      const w = metresBetween(coords[i], coords[i + 1]);
      addEdge(a, b, w);
      addEdge(b, a, w);
    }
  });

  // 2. Bridge trails that meet at a junction but don't share an exact vertex.
  //    Each trail endpoint is projected onto every other trail; if it lands
  //    within tolerance, we stitch the endpoint into that trail's nearest edge
  //    so the network is connected there and routing can cross between trails.
  segments.forEach((coords, si) => {
    if (!coords || coords.length < 2) return;
    const endpoints = [coords[0], coords[coords.length - 1]];
    endpoints.forEach((ep) => {
      const epPoint = turf.point([ep[0], ep[1]]);
      segments.forEach((other, oi) => {
        if (oi === si || !other || other.length < 2) return;
        let np;
        try {
          np = turf.nearestPointOnLine(turf.lineString(other.map((c) => [c[0], c[1]])), epPoint);
        } catch (e) {
          return;
        }
        const gap = turf.distance(epPoint, np, { units: 'meters' });
        if (gap > BRIDGE_TOLERANCE_M) return;

        // Connect the endpoint to the two vertices bracketing the nearest point,
        // effectively inserting it into that trail's edge at the junction.
        const idx = Math.min((np.properties as any).index ?? 0, other.length - 2);
        const epKey = ensureNode(ep);
        [other[idx], other[idx + 1]].forEach((bracket) => {
          const bKey = ensureNode(bracket);
          const w = metresBetween(ep, bracket);
          addEdge(epKey, bKey, w);
          addEdge(bKey, epKey, w);
        });
      });
    });
  });

  return graph;
}

// A small binary min-heap keyed by distance. Stale entries are tolerated: the
// caller skips a popped entry whose distance is worse than the best recorded.
class TrailMinHeap {
  private items: { key: string; dist: number }[] = [];

  get size(): number {
    return this.items.length;
  }

  push(key: string, dist: number): void {
    this.items.push({ key, dist });
    let i = this.items.length - 1;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (this.items[parent].dist <= this.items[i].dist) break;
      [this.items[parent], this.items[i]] = [this.items[i], this.items[parent]];
      i = parent;
    }
  }

  pop(): { key: string; dist: number } | undefined {
    const top = this.items[0];
    const last = this.items.pop();
    if (this.items.length > 0 && last) {
      this.items[0] = last;
      let i = 0;
      const n = this.items.length;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        let smallest = i;
        const l = 2 * i + 1;
        const r = 2 * i + 2;
        if (l < n && this.items[l].dist < this.items[smallest].dist) smallest = l;
        if (r < n && this.items[r].dist < this.items[smallest].dist) smallest = r;
        if (smallest === i) break;
        [this.items[smallest], this.items[i]] = [this.items[i], this.items[smallest]];
        i = smallest;
      }
    }
    return top;
  }
}

// Snap a point to the nearest measurable trail and return the snapped coordinate
// plus the two trail vertices that bracket it (the graph nodes it sits between).
function snapToNearestSegment(
  segments: number[][][],
  lngLat: [number, number]
): { coord: number[]; brackets: number[][] } | null {
  const pt = turf.point(lngLat);
  let best: { coord: number[]; brackets: number[][]; dist: number } | null = null;

  segments.forEach((coords) => {
    if (!coords || coords.length < 2) return;
    const line = turf.lineString(coords.map((c) => [c[0], c[1]]));
    const np = turf.nearestPointOnLine(line, pt);
    const dist = turf.distance(pt, np, { units: 'meters' });
    if (!best || dist < best.dist) {
      const idx = Math.min((np.properties as any).index ?? 0, coords.length - 2);
      best = {
        coord: np.geometry.coordinates,
        brackets: [coords[idx], coords[idx + 1]],
        dist,
      };
    }
  });

  if (!best) return null;
  return { coord: best.coord, brackets: best.brackets };
}

// Find the shortest on-trail path between two arbitrary points by snapping each
// to the trail network and running multi-source Dijkstra between the vertices
// that bracket them. Returns the path coordinates (snapped endpoints included),
// or null when the two points are on disconnected parts of the network.
export function shortestPathBetween(
  graph: TrailGraph,
  segments: number[][][],
  startLngLat: [number, number],
  endLngLat: [number, number]
): number[][] | null {
  if (segments.length === 0) return null;

  const startSnap = snapToNearestSegment(segments, startLngLat);
  const endSnap = snapToNearestSegment(segments, endLngLat);
  if (!startSnap || !endSnap) return null;

  const sources = startSnap.brackets
    .map((c) => ({ key: trailNodeKey(c), w: metresBetween(startSnap.coord, c) }))
    .filter((e) => graph.has(e.key));
  const targets = endSnap.brackets
    .map((c) => ({ key: trailNodeKey(c), w: metresBetween(endSnap.coord, c) }))
    .filter((e) => graph.has(e.key));

  if (sources.length === 0 || targets.length === 0) return null;

  const dist = new Map<string, number>();
  const prev = new Map<string, string | null>();
  const heap = new TrailMinHeap();

  sources.forEach(({ key, w }) => {
    if (!dist.has(key) || w < (dist.get(key) as number)) {
      dist.set(key, w);
      prev.set(key, null);
      heap.push(key, w);
    }
  });

  const targetKeys = new Set(targets.map((t) => t.key));
  let settled = 0;

  while (heap.size > 0) {
    const top = heap.pop();
    if (!top) break;
    const { key, dist: d } = top;
    if (d > (dist.get(key) ?? Infinity)) continue;
    if (targetKeys.has(key)) {
      settled++;
      if (settled >= targetKeys.size) break;
    }
    const node = graph.get(key);
    if (!node) continue;
    for (const edge of node.edges) {
      const nd = d + edge.w;
      if (nd < (dist.get(edge.to) ?? Infinity)) {
        dist.set(edge.to, nd);
        prev.set(edge.to, key);
        heap.push(edge.to, nd);
      }
    }
  }

  // Choose the target bracket that minimises the full distance to the snapped end.
  let bestTarget: string | null = null;
  let bestTotal = Infinity;
  targets.forEach(({ key, w }) => {
    const dv = dist.get(key);
    if (dv !== undefined && dv + w < bestTotal) {
      bestTotal = dv + w;
      bestTarget = key;
    }
  });
  if (bestTarget === null) return null;

  // Walk the predecessor chain back to a source vertex.
  const reversed: string[] = [];
  const seen = new Set<string>();
  let cur: string | null = bestTarget;
  while (cur !== null && !seen.has(cur)) {
    seen.add(cur);
    reversed.push(cur);
    cur = prev.get(cur) ?? null;
  }
  reversed.reverse();

  const pathCoords = reversed.map((k) => (graph.get(k) as TrailGraphNode).coord);
  const result = [startSnap.coord, ...pathCoords, endSnap.coord];

  // Drop consecutive duplicate coordinates introduced by the snapped bookends.
  const deduped: number[][] = [];
  for (const c of result) {
    const last = deduped[deduped.length - 1];
    if (!last || last[0] !== c[0] || last[1] !== c[1]) deduped.push(c);
  }

  return deduped.length >= 2 ? deduped : null;
}

// Helper function to get elevation for coordinates using Hiiker elevation service
async function getElevationForCoordinate(lng: number, lat: number): Promise<number> {
  try {
    const elevation = await singleHiikerElevation(lat, lng);
    if (elevation && elevation.elevation !== undefined) {
      return typeof elevation.elevation === 'string'
        ? parseFloat(elevation.elevation)
        : elevation.elevation;
    }
    return 0;
  } catch (error) {
    console.warn('Failed to get elevation for coordinate:', error);
    return 0;
  }
}

// Helper function to enhance coordinates with elevation data
async function enhanceCoordinatesWithElevation(coordinates: number[][]): Promise<number[][]> {
  const enhancedCoordinates = [];

  for (const coord of coordinates) {
    let enhancedCoord = [...coord];

    // If elevation data is missing or zero, get it from the elevation service
    if (enhancedCoord.length < 3 || enhancedCoord[2] === undefined || enhancedCoord[2] === null || enhancedCoord[2] === 0) {
      const [lng, lat] = enhancedCoord.slice(0, 2);
      const elevation = await getElevationForCoordinate(lng, lat);
      enhancedCoord = [lng, lat, elevation];
    }

    enhancedCoordinates.push(enhancedCoord);
  }

  return enhancedCoordinates;
}

const actions: ActionTree<HikeMeasurementState, RootState> = {
  // Start measurement mode
  startMeasurementMode({ commit, state, dispatch }) {
    commit('setMeasurementMode', true);
    commit('setIsActive', true);

    // Remove any existing click handler first to prevent duplicates
    if (clickHandler) {
      window.map.off('click', clickHandler);
    }

    // Set up map click handler for measurement
    clickHandler = (e) => {
      dispatch('mapClicked', e);
    };
    window.map.on('click', clickHandler);
  },

  // Stop measurement mode
  stopMeasurementMode({ commit, state }) {
    commit('setMeasurementMode', false);
    commit('setIsActive', false);
    commit('setMeasuredGeoJson', null);

    // Remove our specific map click handler
    if (clickHandler) {
      window.map.off('click', clickHandler);
      clickHandler = null;
    }

    // Remove markers
    markers.forEach((marker) => {
      marker.remove();
    });
    markers = [];
    markerElevations = [];

    // Remove measurement line from map
    if (window.map.getLayer('hike-measurement')) {
      window.map.removeLayer('hike-measurement');
    }
    if (window.map.getSource('hike-measurement')) {
      window.map.removeSource('hike-measurement');
    }
  },

  // Set the measureable GeoJSON data
  setMeasureableGeoJson({ commit }, geoJson: any) {
    commit('setMeasureableGeoJson', geoJson);
  },

  // Handle map click in measurement mode
  mapClicked({ state, dispatch }, event: mapboxgl.MapMouseEvent) {
    if (!state.measurementMode) return;

    dispatch('addMeasurementMarkerToMap', event);
  },

  // Add measurement marker to map
  async addMeasurementMarkerToMap({ state, getters, dispatch }, event: mapboxgl.MapMouseEvent) {
    if (markers.length >= 2) {
      return;
    }

    const markerOption: mapboxgl.MarkerOptions = {
      element: markers.length === 0 ? getters.marker1Element : getters.marker2Element,
      draggable: true,
      anchor: 'center',
      offset: [0, -12]
    };

    // Convert click point to turf point
    let point = turf.point(event.lngLat.toArray());

    // Find the closest point on any of the multiline strings
    let closestPoint = null;
    let closestCoordinates = null;
    let minDistance = Infinity;

    // Iterate through all MultiLineString features to find the closest point on
    // any measurable route (main route, alts and spurs)
    state.measureableGeoJson.features.forEach((feature: any) => {
      if (feature.geometry.type === 'MultiLineString') {
        const routeStyle = feature.properties?.routeStyle || 'route';
        // Snap to main routes, alts and spurs; exclude transport connectors
        if (isMeasurableRouteStyle(routeStyle)) {
          feature.geometry.coordinates.forEach((lineString: any) => {
            const line = turf.lineString(lineString);
            const pointOnLine = turf.nearestPointOnLine(line, point);
            const distance = turf.distance(point, pointOnLine, { units: 'meters' });

            if (distance < minDistance) {
              minDistance = distance;
              closestPoint = pointOnLine;
              // Store the actual coordinates from the line (which include elevation)
              closestCoordinates = lineString;
            }
          });
        }
      }
    });

    if (!closestPoint) return;

    // Get the snapped coordinates (without elevation from turf)
    const snappedCoords = closestPoint.geometry.coordinates;

    // Interpolate elevation from the closest line segment
    let finalCoords = [...snappedCoords];
    let elevationFound = false;

    if (closestCoordinates && closestCoordinates.length >= 2) {
      // Find the two closest points on the line that bracket our snapped point
      let minDistance1 = Infinity;
      let minDistance2 = Infinity;
      let closestPoint1 = null;
      let closestPoint2 = null;
      let closestIndex1 = -1;

      closestCoordinates.forEach((coord: number[], index: number) => {
        const coordPoint = turf.point([coord[0], coord[1]]);
        const snappedPoint = turf.point([snappedCoords[0], snappedCoords[1]]);
        const distance = turf.distance(coordPoint, snappedPoint, { units: 'meters' });

        if (distance < minDistance1) {
          // Shift the previous closest to second closest
          minDistance2 = minDistance1;
          closestPoint2 = closestPoint1;

          // Update closest
          minDistance1 = distance;
          closestPoint1 = coord;
          closestIndex1 = index;
        } else if (distance < minDistance2) {
          minDistance2 = distance;
          closestPoint2 = coord;
        }
      });

      // If we found two points with elevation data, interpolate between them
      if (closestPoint1 && closestPoint2 &&
          closestPoint1.length >= 3 && closestPoint2.length >= 3 &&
          closestPoint1[2] !== undefined && closestPoint1[2] !== null &&
          closestPoint2[2] !== undefined && closestPoint2[2] !== null) {

        // Calculate distances for interpolation
        const totalDistance = turf.distance(
          turf.point([closestPoint1[0], closestPoint1[1]]),
          turf.point([closestPoint2[0], closestPoint2[1]]),
          { units: 'meters' }
        );

        const distanceToPoint1 = turf.distance(
          turf.point([snappedCoords[0], snappedCoords[1]]),
          turf.point([closestPoint1[0], closestPoint1[1]]),
          { units: 'meters' }
        );

        // Interpolate elevation
        if (totalDistance > 0) {
          const fraction = distanceToPoint1 / totalDistance;
          const interpolatedElevation = closestPoint1[2] + (closestPoint2[2] - closestPoint1[2]) * fraction;
          finalCoords = [snappedCoords[0], snappedCoords[1], interpolatedElevation];
          elevationFound = true;
        } else {
          // Points are at the same location, use the elevation from the closest point
          finalCoords = [snappedCoords[0], snappedCoords[1], closestPoint1[2]];
          elevationFound = true;
        }
      } else if (closestPoint1 && closestPoint1.length >= 3 &&
                 closestPoint1[2] !== undefined && closestPoint1[2] !== null) {
        // If we only found one point with elevation, use it
        finalCoords = [snappedCoords[0], snappedCoords[1], closestPoint1[2]];
        elevationFound = true;
      }
    }

    // Fall back to elevation service if we couldn't interpolate from route data
    if (!elevationFound) {
      const [lng, lat] = finalCoords.slice(0, 2);
      const elevation = await getElevationForCoordinate(lng, lat);
      finalCoords = [lng, lat, elevation];
    }

    const marker = new mapboxgl.Marker(markerOption).setLngLat(
      // @ts-ignore
      finalCoords.slice(0, 2)
    ).addTo(
      window.map
    ).on('dragend', async (e) => {
      await dispatch('measurementMarkerDragged', e);
    });

    markers.push(marker);
    markerElevations.push(finalCoords[2] || 0); // Store the elevation
    await dispatch('updateMeasurementLine');
  },

  // Handle marker drag
  async measurementMarkerDragged({ dispatch }, event: any) {
    let markerLocation = event.target.getLngLat();

    // Convert the marker location to a GeoJSON Point object
    let point = turf.point([markerLocation.lng, markerLocation.lat]);

    // Find the closest point on the multiline strings
    let closestPoint = null;
    let closestCoordinates = null;
    let minDistance = Infinity;

    // @ts-ignore
    const state = this.state.hikeMeasurement;

    state.measureableGeoJson.features.forEach((feature: any) => {
      if (feature.geometry.type === 'MultiLineString') {
        const routeStyle = feature.properties?.routeStyle || 'route';
        // Snap to main routes, alts and spurs; exclude transport connectors
        if (isMeasurableRouteStyle(routeStyle)) {
          feature.geometry.coordinates.forEach((lineString: any) => {
            const line = turf.lineString(lineString);
            const pointOnLine = turf.nearestPointOnLine(line, point);
            const distance = turf.distance(point, pointOnLine, { units: 'meters' });

            if (distance < minDistance) {
              minDistance = distance;
              closestPoint = pointOnLine;
              // Store the actual coordinates from the line (which include elevation)
              closestCoordinates = lineString;
            }
          });
        }
      }
    });

    if (closestPoint) {
      // Get the snapped coordinates (without elevation from turf)
      const snappedCoords = closestPoint.geometry.coordinates;

      // Interpolate elevation from the closest line segment
      let finalCoords = [...snappedCoords];
      let elevationFound = false;

      if (closestCoordinates && closestCoordinates.length >= 2) {
        // Find the two closest points on the line that bracket our snapped point
        let minDistance1 = Infinity;
        let minDistance2 = Infinity;
        let closestPoint1 = null;
        let closestPoint2 = null;
        let closestIndex1 = -1;

        closestCoordinates.forEach((coord: number[], index: number) => {
          const coordPoint = turf.point([coord[0], coord[1]]);
          const snappedPoint = turf.point([snappedCoords[0], snappedCoords[1]]);
          const distance = turf.distance(coordPoint, snappedPoint, { units: 'meters' });

          if (distance < minDistance1) {
            // Shift the previous closest to second closest
            minDistance2 = minDistance1;
            closestPoint2 = closestPoint1;

            // Update closest
            minDistance1 = distance;
            closestPoint1 = coord;
            closestIndex1 = index;
          } else if (distance < minDistance2) {
            minDistance2 = distance;
            closestPoint2 = coord;
          }
        });

        // If we found two points with elevation data, interpolate between them
        if (closestPoint1 && closestPoint2 &&
            closestPoint1.length >= 3 && closestPoint2.length >= 3 &&
            closestPoint1[2] !== undefined && closestPoint1[2] !== null &&
            closestPoint2[2] !== undefined && closestPoint2[2] !== null) {

          // Calculate distances for interpolation
          const totalDistance = turf.distance(
            turf.point([closestPoint1[0], closestPoint1[1]]),
            turf.point([closestPoint2[0], closestPoint2[1]]),
            { units: 'meters' }
          );

          const distanceToPoint1 = turf.distance(
            turf.point([snappedCoords[0], snappedCoords[1]]),
            turf.point([closestPoint1[0], closestPoint1[1]]),
            { units: 'meters' }
          );

          // Interpolate elevation
          if (totalDistance > 0) {
            const fraction = distanceToPoint1 / totalDistance;
            const interpolatedElevation = closestPoint1[2] + (closestPoint2[2] - closestPoint1[2]) * fraction;
            finalCoords = [snappedCoords[0], snappedCoords[1], interpolatedElevation];
            elevationFound = true;
          } else {
            // Points are at the same location, use the elevation from the closest point
            finalCoords = [snappedCoords[0], snappedCoords[1], closestPoint1[2]];
            elevationFound = true;
          }
        } else if (closestPoint1 && closestPoint1.length >= 3 &&
                   closestPoint1[2] !== undefined && closestPoint1[2] !== null) {
          // If we only found one point with elevation, use it
          finalCoords = [snappedCoords[0], snappedCoords[1], closestPoint1[2]];
          elevationFound = true;
        }
      }

      // Fall back to elevation service if we couldn't interpolate from route data
      if (!elevationFound) {
        const [lng, lat] = finalCoords.slice(0, 2);
        const elevation = await getElevationForCoordinate(lng, lat);
        finalCoords = [lng, lat, elevation];
      }

      // @ts-ignore
      event.target.setLngLat(finalCoords.slice(0, 2));

      // Update the elevation for this marker
      const markerIndex = markers.indexOf(event.target);
      if (markerIndex !== -1) {
        markerElevations[markerIndex] = finalCoords[2] || 0;
      }

      await dispatch('updateMeasurementLine');
    }
  },

  // Set hover coordinate for elevation profile interaction
  setHoverCoordinate({ commit }, coordinate: [number, number] | null) {
    commit('setHoverCoordinate', coordinate);
  },

  // Update the measurement line between markers
  async updateMeasurementLine({ state, commit }) {
    if (markers.length < 2) return;

    const location1 = turf.point(markers[0].getLngLat().toArray());
    const location2 = turf.point(markers[1].getLngLat().toArray());

    console.log('Updating measurement line between:', location1.geometry.coordinates, location2.geometry.coordinates);
    console.log('Number of markers:', markers.length);

    let measurementLine = null;

    // First, try to find the continuous route between the two points
    // by tracing through all line segments in the MultiLineString
    const allCoordinates = [];
    let foundStart = false;
    let foundEnd = false;
    let currentSegment = null;

    // Collect all line segments from the MultiLineString for measurable routes
    // (main route, alts and spurs); exclude transport connectors
    const allLineSegments = [];
    state.measureableGeoJson.features.forEach((feature: any) => {
      if (feature.geometry.type === 'MultiLineString') {
        const routeStyle = feature.properties?.routeStyle || 'route';
        if (isMeasurableRouteStyle(routeStyle)) {
          feature.geometry.coordinates.forEach((lineString: any) => {
            allLineSegments.push(turf.lineString(lineString));
          });
        } else {
          console.log('Skipping route with style:', routeStyle);
        }
      }
    });

    // Find which segments contain the start and end points
    let startSegment = null;
    let endSegment = null;
    let startSegmentIndex = -1;
    let endSegmentIndex = -1;
    let startMinDistance = Infinity;
    let endMinDistance = Infinity;

    console.log('Total line segments:', allLineSegments.length);

    allLineSegments.forEach((segment, index) => {
      // Use nearestPointOnLine to find the closest point on this segment to each marker
      try {
        const startPointOnLine = turf.nearestPointOnLine(segment, location1);
        const startDistance = turf.distance(location1, startPointOnLine, { units: 'meters' });

        // If this segment is closer to the start marker than previous segments, use it
        if (startDistance < startMinDistance) {
          startMinDistance = startDistance;
          startSegment = segment;
          startSegmentIndex = index;
          console.log('Found closer start segment at index:', index, 'distance:', startDistance);
        }
      } catch (e) {
        console.log('Error testing start segment at index', index, e.message);
      }

      try {
        const endPointOnLine = turf.nearestPointOnLine(segment, location2);
        const endDistance = turf.distance(location2, endPointOnLine, { units: 'meters' });

        // If this segment is closer to the end marker than previous segments, use it
        if (endDistance < endMinDistance) {
          endMinDistance = endDistance;
          endSegment = segment;
          endSegmentIndex = index;
          console.log('Found closer end segment at index:', index, 'distance:', endDistance);
        }
      } catch (e) {
        console.log('Error testing end segment at index', index, e.message);
      }
    });

    console.log('Start segment found:', !!startSegment, 'at index:', startSegmentIndex);
    console.log('End segment found:', !!endSegment, 'at index:', endSegmentIndex);

    if (startSegment && endSegment) {
      // Both points are on valid segments
      if (startSegmentIndex === endSegmentIndex) {
        // Both points are on the same segment
        console.log('Both points on same segment');
        try {
          // Get the actual snapped coordinates for both points on this segment
          const startPointOnLine = turf.nearestPointOnLine(startSegment, location1);
          const endPointOnLine = turf.nearestPointOnLine(startSegment, location2);

          // Determine the correct order based on the segment's natural direction
          // Find the indices of the closest points in the original segment coordinates
          const segmentCoords = startSegment.geometry.coordinates;
          let startIndex = -1;
          let endIndex = -1;
          let minStartDistance = Infinity;
          let minEndDistance = Infinity;

          // Find the closest coordinate indices for both points
          segmentCoords.forEach((coord: number[], index: number) => {
            const coordPoint = turf.point(coord);
            const startDistance = turf.distance(startPointOnLine, coordPoint, { units: 'meters' });
            const endDistance = turf.distance(endPointOnLine, coordPoint, { units: 'meters' });

            if (startDistance < minStartDistance) {
              minStartDistance = startDistance;
              startIndex = index;
            }
            if (endDistance < minEndDistance) {
              minEndDistance = endDistance;
              endIndex = index;
            }
          });

          console.log('Start index:', startIndex, 'End index:', endIndex);

          // Ensure we always measure in the direction of the route
          let slice;
          if (startIndex <= endIndex) {
            // Normal direction: start comes before end in the route
            slice = turf.lineSlice(startPointOnLine, endPointOnLine, startSegment);
          } else {
            // Reverse direction: end comes before start in the route
            // Use the segment from end to start, then reverse it
            slice = turf.lineSlice(endPointOnLine, startPointOnLine, startSegment);
            if (slice && slice.geometry.coordinates.length > 1) {
              // Reverse the coordinates to maintain start-to-end direction
              slice.geometry.coordinates.reverse();
            }
          }

          if (slice && slice.geometry.coordinates.length > 1) {
            // Ensure all coordinates in the slice have elevation data
            const enhancedCoordinates = await enhanceCoordinatesWithElevation(slice.geometry.coordinates);

            // Explicitly ensure start and end points have elevation from the service
            // (nearestPointOnLine doesn't preserve elevation)
            const startCoord = enhancedCoordinates[0];
            const endCoord = enhancedCoordinates[enhancedCoordinates.length - 1];

            if (!startCoord[2] || startCoord[2] === 0) {
              startCoord[2] = await getElevationForCoordinate(startCoord[0], startCoord[1]);
            }
            if (!endCoord[2] || endCoord[2] === 0) {
              endCoord[2] = await getElevationForCoordinate(endCoord[0], endCoord[1]);
            }

            measurementLine = turf.lineString(enhancedCoordinates);
            console.log('Created measurement line from single segment with enhanced elevation data');
          }
        } catch (e) {
          console.log('Error creating slice from single segment:', e.message);
          // Fall through to fallback
        }
      } else {
        // Points are on different segments — route across the whole trail network
        // (main route + alts + spurs treated as one connected graph) so a
        // measurement can flow from one trail onto another at the junction where
        // they meet, instead of jumping between disconnected segment ends.
        console.log('Points on different segments, routing across trail network');
        const networkPath = shortestPathBetween(
          getMeasurementGraph(state.measureableGeoJson, allLineSegments),
          allLineSegments.map((s: any) => s.geometry.coordinates),
          markers[0].getLngLat().toArray() as [number, number],
          markers[1].getLngLat().toArray() as [number, number]
        );
        if (networkPath && networkPath.length >= 2) {
          measurementLine = turf.lineString(await enhanceCoordinatesWithElevation(networkPath));
          console.log('Created measurement line from trail network path with', networkPath.length, 'points');
        }

        // Legacy segment-by-segment tracing — only used as a fallback when the
        // trail network is disconnected and routing above could not find a path.
        const segmentsInOrder = [];
        if (!measurementLine) {

        // Get the actual snapped coordinates for both points on their respective segments
        const startPointOnLine = turf.nearestPointOnLine(startSegment, location1);
        const endPointOnLine = turf.nearestPointOnLine(endSegment, location2);

        // Check if we're measuring forwards or backwards along the route
        const measuringForward = startSegmentIndex <= endSegmentIndex;

        console.log('Start segment index:', startSegmentIndex, 'End segment index:', endSegmentIndex);
        console.log('Measuring forward:', measuringForward);

        if (measuringForward) {
          // Normal direction: marker 1 is before marker 2 along the route
          // Add the start segment (from start point to end of segment)
          try {
            const startSlice = turf.lineSlice(startPointOnLine, turf.point(startSegment.geometry.coordinates[startSegment.geometry.coordinates.length - 1]), startSegment);
            if (startSlice && startSlice.geometry.coordinates.length > 1) {
              segmentsInOrder.push(startSlice);
            }
          } catch (e) {}

          // Add all intermediate segments between start and end
          for (let i = startSegmentIndex + 1; i < endSegmentIndex; i++) {
            const intermediateSegment = allLineSegments[i];
            // Ensure intermediate segments have elevation data
            if (intermediateSegment && intermediateSegment.geometry && intermediateSegment.geometry.coordinates) {
              const enhancedCoords = await enhanceCoordinatesWithElevation(intermediateSegment.geometry.coordinates);
              segmentsInOrder.push(turf.lineString(enhancedCoords));
            } else {
              segmentsInOrder.push(intermediateSegment);
            }
          }

          // Add the end segment (from start of segment to end point)
          try {
            const endSlice = turf.lineSlice(turf.point(endSegment.geometry.coordinates[0]), endPointOnLine, endSegment);
            if (endSlice && endSlice.geometry.coordinates.length > 1) {
              segmentsInOrder.push(endSlice);
            }
          } catch (e) {}
        } else {
          // Reverse direction: marker 1 is after marker 2 along the route
          // We need to trace backwards from marker 1 to marker 2
          // Add the start segment (from start point backwards to beginning of segment)
          try {
            const startSlice = turf.lineSlice(turf.point(startSegment.geometry.coordinates[0]), startPointOnLine, startSegment);
            if (startSlice && startSlice.geometry.coordinates.length > 1) {
              // Reverse the coordinates to go backwards
              const reversedCoords = [...startSlice.geometry.coordinates].reverse();
              segmentsInOrder.push(turf.lineString(reversedCoords));
            }
          } catch (e) {}

          // Add all intermediate segments between start and end (in reverse)
          for (let i = startSegmentIndex - 1; i > endSegmentIndex; i--) {
            const intermediateSegment = allLineSegments[i];
            // Ensure intermediate segments have elevation data
            if (intermediateSegment && intermediateSegment.geometry && intermediateSegment.geometry.coordinates) {
              const enhancedCoords = await enhanceCoordinatesWithElevation(intermediateSegment.geometry.coordinates);
              // Reverse the coordinates to go backwards
              const reversedCoords = [...enhancedCoords].reverse();
              segmentsInOrder.push(turf.lineString(reversedCoords));
            } else if (intermediateSegment) {
              // Reverse the coordinates
              const reversedCoords = [...intermediateSegment.geometry.coordinates].reverse();
              segmentsInOrder.push(turf.lineString(reversedCoords));
            }
          }

          // Add the end segment (from end of segment backwards to end point)
          try {
            const endSlice = turf.lineSlice(endPointOnLine, turf.point(endSegment.geometry.coordinates[endSegment.geometry.coordinates.length - 1]), endSegment);
            if (endSlice && endSlice.geometry.coordinates.length > 1) {
              // Reverse the coordinates to go backwards
              const reversedCoords = [...endSlice.geometry.coordinates].reverse();
              segmentsInOrder.push(turf.lineString(reversedCoords));
            }
          } catch (e) {}
        }

        // Combine all segments into a continuous line with straight line connectors
        if (segmentsInOrder.length > 0) {
          console.log('Combining', segmentsInOrder.length, 'segments');
          const combinedCoordinates = [];

          // Helper function to create straight line connector with elevation data consistency
          const createStraightLineConnector = async (startCoord: number[], endCoord: number[]): Promise<number[][]> => {
            const connectorPoints = [];

            // Calculate distance between points
            const distance = turf.distance(turf.point(startCoord), turf.point(endCoord), { units: 'meters' });
            console.log('Creating connector, distance:', distance, 'meters');

            // Use the same elevation data consistency logic as marker placement
            const enhanceCoordinateWithRouteElevation = async (coord: number[]): Promise<number[]> => {
              let enhancedCoord = [...coord];

              // If elevation data is missing, try to find route elevation data first
              if (enhancedCoord.length < 3 || enhancedCoord[2] === undefined || enhancedCoord[2] === null) {
                const [lng, lat] = enhancedCoord.slice(0, 2);
                let foundRouteElevation = false;
                let closestElevationPoint = null;
                let minElevationDistance = Infinity;

                // Search through all route features for nearby coordinates with elevation data
                state.measureableGeoJson.features.forEach((feature: any) => {
                  if (feature.geometry.type === 'MultiLineString') {
                    const routeStyle = feature.properties?.routeStyle || 'route';
                    if (isMeasurableRouteStyle(routeStyle)) {
                      feature.geometry.coordinates.forEach((lineString: any) => {
                        lineString.forEach((routeCoord: number[]) => {
                          if (routeCoord.length >= 3 && routeCoord[2] !== undefined && routeCoord[2] !== null) {
                            const routePoint = turf.point([routeCoord[0], routeCoord[1]]);
                            const currentPoint = turf.point([lng, lat]);
                            const routeDistance = turf.distance(routePoint, currentPoint, { units: 'meters' });

                            if (routeDistance < minElevationDistance && routeDistance < 50) {
                              minElevationDistance = routeDistance;
                              closestElevationPoint = routeCoord;
                              foundRouteElevation = true;
                            }
                          }
                        });
                      });
                    }
                  }
                });

                // If we found a nearby coordinate with elevation data, use it
                if (foundRouteElevation && closestElevationPoint) {
                  enhancedCoord = [lng, lat, closestElevationPoint[2]];
                } else {
                  // Fall back to elevation service
                  const elevation = await getElevationForCoordinate(lng, lat);
                  enhancedCoord = [lng, lat, elevation];
                }
              }

              return enhancedCoord;
            };

            // Enhance both coordinates with elevation data consistency
            const enhancedStartCoord = await enhanceCoordinateWithRouteElevation(startCoord);
            const enhancedEndCoord = await enhanceCoordinateWithRouteElevation(endCoord);

            // Add intermediate points for better visualization (every 50 meters)
            const numIntermediatePoints = Math.max(1, Math.floor(distance / 50));

            for (let i = 1; i <= numIntermediatePoints; i++) {
              const fraction = i / (numIntermediatePoints + 1);
              const intermediateLng = enhancedStartCoord[0] + (enhancedEndCoord[0] - enhancedStartCoord[0]) * fraction;
              const intermediateLat = enhancedStartCoord[1] + (enhancedEndCoord[1] - enhancedStartCoord[1]) * fraction;

              // Interpolate elevation between start and end points
              const intermediateElevation = enhancedStartCoord[2] + (enhancedEndCoord[2] - enhancedStartCoord[2]) * fraction;

              connectorPoints.push([intermediateLng, intermediateLat, intermediateElevation]);
            }

            console.log('Created', connectorPoints.length, 'connector points with elevation interpolation');
            return connectorPoints;
          };

          // Start with the actual start marker position and use the stored elevation
          const startMarkerPosition = markers[0].getLngLat().toArray();
          const startMarkerWithElevation = markerElevations[0] || 0;

          combinedCoordinates.push([startMarkerPosition[0], startMarkerPosition[1], startMarkerWithElevation]);

          for (let index = 0; index < segmentsInOrder.length; index++) {
            const segment = segmentsInOrder[index];
            if (segment.geometry && segment.geometry.coordinates) {
              // Ensure segment coordinates have elevation data before adding them
              const enhancedCoords = await enhanceCoordinatesWithElevation(segment.geometry.coordinates);

              if (index === 0) {
                // First segment - add all enhanced coordinates
                combinedCoordinates.push(...enhancedCoords);
              } else {
                // Add a straight line connector from previous segment to current segment
                const previousSegment = segmentsInOrder[index - 1];
                if (previousSegment && previousSegment.geometry && previousSegment.geometry.coordinates) {
                  // Get the enhanced coordinates for the previous segment to ensure elevation data
                  const previousEnhancedCoords = await enhanceCoordinatesWithElevation(previousSegment.geometry.coordinates);
                  const previousEnd = previousEnhancedCoords[previousEnhancedCoords.length - 1];
                  const currentStart = enhancedCoords[0];

                  // Create straight line connector between segments
                  if (previousEnd && currentStart) {
                    // Add connector line with intermediate points for better visualization
                    const connector = await createStraightLineConnector(previousEnd, currentStart);
                    combinedCoordinates.push(...connector);
                  }
                }

                // Add the current segment enhanced coordinates
                combinedCoordinates.push(...enhancedCoords);
              }
            }
          }

          // End with the actual end marker position and use the stored elevation
          const endMarkerPosition = markers[1].getLngLat().toArray();
          const endMarkerWithElevation = markerElevations[1] || 0;

          combinedCoordinates.push([endMarkerPosition[0], endMarkerPosition[1], endMarkerWithElevation]);

          if (combinedCoordinates.length > 1) {
            console.log('Created combined coordinates with', combinedCoordinates.length, 'points');
            // Ensure all coordinates have elevation data
            const enhancedCoordinates = await enhanceCoordinatesWithElevation(combinedCoordinates);
            measurementLine = turf.lineString(enhancedCoordinates);
          } else {
            console.log('Not enough combined coordinates:', combinedCoordinates.length);
          }
        } else {
          console.log('No segments to combine');
        }
        } // end legacy fallback (network routing failed)
      }
    }

    console.log('Measurement line created:', !!measurementLine);

    if (!measurementLine) {
      console.log('Using fallback measurement line');
      // Fallback: create a direct line between points with elevation data
      // Find the closest points on the route to get elevation data
      let startPointWithElevation = null;
      let endPointWithElevation = null;

      state.measureableGeoJson.features.forEach((feature: any) => {
        if (feature.geometry.type === 'MultiLineString') {
          const routeStyle = feature.properties?.routeStyle || 'route';
          // Include main routes, alts and spurs; exclude transport connectors
          if (isMeasurableRouteStyle(routeStyle)) {
            feature.geometry.coordinates.forEach((lineString: any) => {
              const line = turf.lineString(lineString);

              // Find closest point for start marker
              const startPointOnLine = turf.nearestPointOnLine(line, location1);
              if (!startPointWithElevation || turf.distance(location1, startPointOnLine) < turf.distance(location1, startPointWithElevation)) {
                startPointWithElevation = startPointOnLine;
              }

              // Find closest point for end marker
              const endPointOnLine = turf.nearestPointOnLine(line, location2);
              if (!endPointWithElevation || turf.distance(location2, endPointOnLine) < turf.distance(location2, endPointWithElevation)) {
                endPointWithElevation = endPointOnLine;
              }
            });
          }
        }
      });

      // Use the stored marker elevations for the fallback direct line
      const startMarkerPos = markers[0].getLngLat().toArray();
      const endMarkerPos = markers[1].getLngLat().toArray();
      const startCoords = [startMarkerPos[0], startMarkerPos[1], markerElevations[0] || 0];
      const endCoords = [endMarkerPos[0], endMarkerPos[1], markerElevations[1] || 0];

      measurementLine = turf.lineString([startCoords, endCoords]);
    }

    commit('setMeasuredGeoJson', measurementLine);

    const lineLayerId = 'hike-measurement';
    const map = window.map;
    if (!map) return;

    // Remove existing measurement layer
    if (map.getLayer(lineLayerId)) {
      map.removeLayer(lineLayerId);
    }
    if (map.getSource(lineLayerId)) {
      map.removeSource(lineLayerId);
    }

    // Add new measurement layer
    let source: mapboxgl.GeoJSONSource = {
      type: 'geojson',
      // @ts-ignore
      data: measurementLine
    };

    map.addSource(lineLayerId, source);
    map.addLayer({
      id: lineLayerId,
      type: 'line',
      source: lineLayerId,
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': HiikerColor.green,
        'line-width': 5,
      }
    });
  },

  // Re-add measurement line to map after style change
  readdMeasurementToMap({ state }) {
    const lineLayerId = 'hike-measurement';
    const map = window.map;

    if (!map || !state.measuredGeoJson) return;

    // Remove existing measurement layer if present
    if (map.getLayer(lineLayerId)) {
      map.removeLayer(lineLayerId);
    }
    if (map.getSource(lineLayerId)) {
      map.removeSource(lineLayerId);
    }

    // Re-add measurement layer with the existing measured GeoJSON
    let source: mapboxgl.GeoJSONSource = {
      type: 'geojson',
      // @ts-ignore
      data: state.measuredGeoJson
    };

    map.addSource(lineLayerId, source);
    map.addLayer({
      id: lineLayerId,
      type: 'line',
      source: lineLayerId,
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': HiikerColor.green,
        'line-width': 5,
      }
    });
  }
};

const mutations: MutationTree<HikeMeasurementState> = {
  setMeasurementMode(state, measurementMode: boolean) {
    state.measurementMode = measurementMode;
  },

  setMeasuredGeoJson(state, geoJson: any) {
    state.measuredGeoJson = geoJson;
  },

  setMeasureableGeoJson(state, geoJson: any) {
    state.measureableGeoJson = geoJson;
  },

  setIsActive(state, isActive: boolean) {
    state.isActive = isActive;
  },

  setHoverCoordinate(state, coordinate: [number, number] | null) {
    state.hoverCoordinate = coordinate;
  }
};

const getters: GetterTree<HikeMeasurementState, RootState> = {
  // Get formatted distance
  measuredDistance(state) {
    if (!state.measuredGeoJson) return '0 m';

    const distance = turf.length(state.measuredGeoJson, { units: 'meters' });
    const measurementPreference = MeasurementPrefUtility.preference();
    return measurementPreference.smallUnitFormatter(distance, true);
  },

  // Get formatted elevation gain
  measuredElevationGain(state) {
    if (!state.measuredGeoJson) return '0 m';

    const measurementPreference = MeasurementPrefUtility.preference();
    const coordinates = state.measuredGeoJson.geometry.coordinates;

    if (coordinates.length < 2) return '0 m';

    // Use the same 6m-threshold algorithm as the route planner so the measured
    // elevation gain matches what the planner shows after "Create Hike".
    const elevationGain = elevationGainFromCoordinates(coordinates);

    return measurementPreference.smallUnitFormatter(elevationGain);
  },

  // Get formatted hike time
  measuredHikeTime(state) {
    if (!state.measuredGeoJson) return '~ 0 mins';

    const distance = turf.length(state.measuredGeoJson, { units: 'kilometers' });
    const coordinates = state.measuredGeoJson.geometry.coordinates;

    if (coordinates.length < 2) return '~ 0 mins';

    // Same 6m-threshold gain as the displayed value / the planner.
    const elevationGain = elevationGainFromCoordinates(coordinates);

    let timeToReturn = 0.0;
    const hikeTime = HikeTimeUtility.preference();
    timeToReturn += (distance / hikeTime.kmDistance());
    timeToReturn += (elevationGain / hikeTime.meterElevationGain());

    let seconds = (timeToReturn * 60) * 60;
    let hours = Math.floor(seconds / 3600);
    let minutes = Math.floor((seconds % 3600) / 60);
    let timeString = "";

    if (hours > 0) {
      timeString += hours + (hours > 1 ? " hours " : " hour ");
    }
    if (minutes > 0) {
      timeString += minutes + (minutes > 1 ? " mins" : " min");
    }

    return "~ " + (timeString || "0 mins");
  },

  // Get measurement mode status
  isMeasurementMode(state) {
    return state.measurementMode;
  },

  // Get active status
  isActive(state) {
    return state.isActive;
  },

  // Get raw measured GeoJSON data for elevation profile
  measuredGeoJson(state) {
    return state.measuredGeoJson;
  },

  // Get hover coordinate
  hoverCoordinate(state) {
    return state.hoverCoordinate;
  },

  // Marker elements
  marker1Element() {
    const el = document.createElement('div');
    el.id = 'hike-marker-' + Math.random();
    el.style.position = 'absolute';
    el.style.width = `${26}px`;
    el.style.height = `${83}px`;
    el.style.backgroundSize = 'contain';
    el.style.backgroundRepeat = 'no-repeat';
    el.style.cursor = 'pointer';
    el.style.backgroundPosition = 'center center';
    el.style.backgroundImage = `url(${measurementMarker})`;
    return el;
  },

  marker2Element() {
    const el = document.createElement('div');
    el.id = 'hike-marker-' + Math.random();
    el.style.position = 'absolute';
    el.style.width = `${26}px`;
    el.style.height = `${83}px`;
    el.style.backgroundSize = 'contain';
    el.style.backgroundRepeat = 'no-repeat';
    el.style.cursor = 'pointer';
    el.style.backgroundPosition = 'center center';
    el.style.backgroundImage = `url(${measurementMarker})`;
    return el;
  }
};

const state: HikeMeasurementState = new HikeMeasurementState();

export default class HikeMeasurementModule implements Module<HikeMeasurementState, RootState> {
  namespaced = true
  state = state
  actions = actions
  mutations = mutations
  getters = getters
}
